// Article data for /learn and article pages.
// Each article has plain-language body content + sources.
export const ARTICLES = [
  {
    slug: "cortisol-face",
    title: "What \u201Ccortisol face\u201D actually means",
    excerpt: "A viral term for facial puffiness. Here's what the actual research on cortisol and skin says \u2014 and what it doesn't.",
    category: "Myth-busting",
    readTime: "5 min read",
    image: "https://images.pexels.com/photos/6129444/pexels-photo-6129444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "\u201CCortisol face\u201D became a viral term describing puffy cheeks and a rounder facial appearance, blamed on chronic stress. The idea has a kernel of truth, but the internet version is oversimplified. Here's what the evidence supports \u2014 and what it doesn't.",
    body: [
      {
        heading: "What cortisol actually does",
        text:
          "Cortisol is a hormone released by the adrenal glands. It regulates blood sugar, immune response, blood pressure, and the body's response to stress. Short-term rises in cortisol are normal and healthy \u2014 they help you wake up, respond to challenges, and recover from illness.",
      },
      {
        heading: "The link to facial appearance",
        text:
          "Sustained, pathologically high cortisol \u2014 as seen in Cushing's syndrome \u2014 can cause the classic \u201Cmoon face\u201D: a rounded, fuller facial appearance from fat redistribution and fluid retention. But this is a medical condition diagnosed by blood, urine, and imaging tests, not a lifestyle-driven effect from being stressed at work.",
      },
      {
        heading: "What ordinary stress does (and doesn't) do",
        text:
          "Everyday stress can transiently raise cortisol, disturb sleep, and worsen inflammatory skin conditions like acne, eczema, and psoriasis. Poor sleep and high alcohol intake can also cause facial fluid retention that looks like puffiness the next morning. That's real, and often what social media is describing when it says \u201Ccortisol face.\u201D But calling it a cortisol problem specifically overstates what the evidence supports.",
      },
      {
        heading: "The takeaway",
        text:
          "If your face looks puffier when you're sleeping badly, stressed, drinking more, or eating saltier meals \u2014 you're seeing something real, but it's not the same as clinically elevated cortisol. If you have persistent facial swelling, unexplained weight gain, or other symptoms, talk to a healthcare provider. They can order the tests that actually diagnose a cortisol issue.",
      },
    ],
    sources: [
      { text: "Nieman LK, et al. The diagnosis of Cushing's syndrome: an Endocrine Society clinical practice guideline. J Clin Endocrinol Metab. 2008;93(5):1526\u20131540.", url: "https://doi.org/10.1210/jc.2008-0125" },
      { text: "Chen Y, Lyga J. Brain-skin connection: stress, inflammation and skin aging. Inflamm Allergy Drug Targets. 2014;13(3):177\u2013190. \u2014 Note: the authors of this review were employed by a cosmetics company (Avon) at the time of publication.", url: "https://doi.org/10.2174/1871528113666140522104422" },
    ],
  },
  {
    slug: "sleep-and-skin",
    title: "Sleep and your skin: what the research shows",
    excerpt: "Beauty sleep isn't marketing. Here's what controlled studies find about sleep, skin repair, and visible aging.",
    category: "Healthy-aging basics",
    readTime: "6 min read",
    image: "https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "\u201CBeauty sleep\u201D sounds like a marketing phrase, but the research on sleep and skin is more interesting than it seems. Here's what published studies actually show about sleep quality, skin barrier function, and visible aging.",
    body: [
      {
        heading: "Sleep and the skin barrier",
        text:
          "In small controlled studies, poor sleep or sleep deprivation has been linked to reduced skin barrier recovery after damage, slower wound healing, and increased trans-epidermal water loss \u2014 the leaky-barrier signal behind dryness and irritation. The effects are measurable, but modest, and vary between people.",
      },
      {
        heading: "Sleep and visible aging",
        text:
          "A 2015 study by Oyetakin-White and colleagues compared 60 healthy women categorised as good or poor sleepers. Poor sleepers scored higher on a validated skin-ageing scale, showed greater trans-epidermal water loss, and recovered less well from ultraviolet exposure. Two important limits: the study was observational, so it shows association rather than proof of cause, and all participants were Caucasian women \u2014 so the findings cannot be assumed to apply equally across skin tones.",
      },
      {
        heading: "What actually matters",
        text:
          "Total time asleep, sleep consistency (going to bed and waking at similar times), and reducing sleep-disrupting habits (alcohol, late caffeine, screens in bed) matter more than any topical product marketed as \u201Cnight repair.\u201D Sleep is a foundation, not a cosmetic.",
      },
      {
        heading: "The takeaway",
        text:
          "Chronic short sleep is one of the few interventions with plausible, measurable effects on skin function \u2014 and it's free. If your skin looks tired, the fix probably isn't a new serum first.",
      },
    ],
    sources: [
      { text: "Oyetakin-White P, Suggs A, Koo B, et al. Does poor sleep quality affect skin ageing? Clin Exp Dermatol. 2015;40(1):17\u201322.", url: "https://doi.org/10.1111/ced.12455" },
      { text: "Kahan V, Andersen ML, Tomimori J, Tufik S. Can poor sleep affect skin integrity? Med Hypotheses. 2010;75(6):535\u2013537. \u2014 Note: this is a hypothesis paper published in a journal that does not use conventional peer review. It proposes a mechanism; it is not evidence of one.", url: "https://doi.org/10.1016/j.mehy.2010.07.018" },
    ],
  },
  {
    slug: "melanin-rich-skin-sun-protection",
    title: "Melanin-rich skin and sun protection",
    excerpt: "\u201CI don't need sunscreen \u2014 I don't burn.\u201D Here's what the evidence shows about UV, pigmentation, and darker skin tones.",
    category: "Skin science",
    readTime: "6 min read",
    image: "https://images.pexels.com/photos/6621201/pexels-photo-6621201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "\u201CI don't burn, so I don't need sunscreen\u201D is one of the most common assumptions in skincare. Melanin does offer real photoprotection, but the size of that protection \u2014 and what it does and doesn't prevent \u2014 is more nuanced than it sounds.",
    body: [
      {
        heading: "How much protection melanin provides",
        text:
          "Baseline melanin in darker skin tones has been estimated to provide an intrinsic SPF equivalent of roughly 13\u201314, compared with around 3\u20134 in light skin. That's meaningful protection against UV-induced erythema (sunburn) but is far below the SPF 30+ dermatology societies recommend for cumulative exposure.",
      },
      {
        heading: "What the protection doesn't fully cover",
        text:
          "Post-inflammatory hyperpigmentation, melasma, and photoaging can occur on any skin tone \u2014 and darker skin is more prone to persistent pigmentation after inflammation. UVA also penetrates deeper regardless of pigmentation, contributing to collagen breakdown and long-term photoaging.",
      },
      {
        heading: "Skin cancer in darker skin",
        text:
          "Skin cancer is less common on darker skin, but it does occur \u2014 and it is often diagnosed later, when outcomes are worse. Acral melanoma (palms, soles, under nails) is notable because it isn't primarily UV-driven, so \u201CI stay in the shade\u201D isn't a substitute for skin checks.",
      },
      {
        heading: "The takeaway",
        text:
          "Melanin is not a substitute for sunscreen if your goals include even skin tone, reduced hyperpigmentation, and long-term skin-cancer risk reduction. Broad-spectrum SPF 30+, worn consistently, remains what the evidence supports across all skin tones.",
      },
    ],
    sources: [
      { text: "Kaidbey KH, et al. Photoprotection by melanin \u2014 a comparison of black and Caucasian skin. J Am Acad Dermatol. 1979;1(3):249\u2013260.", url: "https://doi.org/10.1016/S0190-9622(79)70018-1" },
      { text: "Agbai ON, et al. Skin cancer and photoprotection in people of color. J Am Acad Dermatol. 2014;70(4):748\u2013762.", url: "https://doi.org/10.1016/j.jaad.2013.11.038" },
    ],
  },
  {
    slug: "inflammaging",
    title: "Understanding \u201Cinflammaging\u201D",
    excerpt: "The idea that chronic, low-grade inflammation accelerates aging. Here's what the science actually claims.",
    category: "Healthy-aging basics",
    readTime: "5 min read",
    image: "https://images.pexels.com/photos/7089629/pexels-photo-7089629.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "\u201CInflammaging\u201D is a real scientific term \u2014 first proposed in 2000 \u2014 describing the chronic, low-grade inflammation that accumulates in tissues as we age. It's not marketing. It's also not a diagnosis you get, or a problem you fix with a supplement.",
    body: [
      {
        heading: "What the term actually means",
        text:
          "Inflammaging refers to the persistent, low-level activation of the immune system that appears with age \u2014 detectable through inflammatory markers like IL-6, TNF-\u03B1, and CRP. It has been linked in research to cardiovascular disease, frailty, cognitive decline, and skin aging.",
      },
      {
        heading: "What drives it",
        text:
          "Contributors identified in the literature include cellular senescence, changes in the gut microbiome, chronic infections, obesity, sleep disruption, and cumulative environmental stress. Genetics matter too. Skin-specific inflammaging is influenced heavily by UV exposure over decades.",
      },
      {
        heading: "What actually helps, according to the research",
        text:
          "Interventions consistently associated with lower inflammatory markers include: regular physical activity, adequate sleep, a diet richer in vegetables and unsaturated fats, not smoking, and photoprotection for skin. These are unglamorous but well-supported.",
      },
      {
        heading: "The takeaway",
        text:
          "Inflammaging is a useful lens for thinking about aging as a whole-body, whole-life process \u2014 not an excuse to buy an \u201Canti-inflammatory\u201D supplement stack. The interventions that matter are the ones your primary-care provider probably already recommends.",
      },
    ],
    sources: [
      { text: "Franceschi C, et al. Inflammaging: a new immune-metabolic viewpoint for age-related diseases. Nat Rev Endocrinol. 2018;14(10):576\u2013590.", url: "https://doi.org/10.1038/s41574-018-0059-4" },
      { text: "Zhuang Y, Lyga J. Inflammaging in skin and other tissues \u2014 the roles of complement system and macrophage. Inflamm Allergy Drug Targets. 2014;13(3):153\u2013161. \u2014 Note: the authors of this review were employed by a cosmetics company (Avon) at the time of publication.", url: "https://doi.org/10.2174/1871528113666140522112003" },
    ],
  },
  {
    slug: "how-to-read-a-skincare-study",
    title: "How to read a skincare study before you buy",
    excerpt: "A short, practical guide to spotting weak evidence and marketing dressed up as science.",
    category: "Myth-busting",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/7092352/pexels-photo-7092352.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "\u201CClinically proven\u201D is a marketing phrase, not a standard. Before spending on a product because \u201Cstudies show,\u201D here's a short checklist for reading the study yourself.",
    body: [
      {
        heading: "1. Who paid for it, and who did it?",
        text:
          "Industry-funded studies aren't automatically invalid, but they should be read with more skepticism, especially when the funder makes and sells the product being tested. Look for an independent university, hospital, or peer-reviewed journal \u2014 not just \u201Cin-house testing.\u201D",
      },
      {
        heading: "2. How many people were in it?",
        text:
          "A study of 12 volunteers over 4 weeks tells you very little. Larger studies (hundreds of participants) with longer follow-up are more reliable. Beware of infographics quoting percentages from tiny samples.",
      },
      {
        heading: "3. Was there a control group?",
        text:
          "Real efficacy studies compare the product to a placebo or an established alternative. \u201C87% of users saw an improvement\u201D means almost nothing without a comparison group \u2014 people rate skin as improved just from paying attention to it.",
      },
      {
        heading: "4. What did they actually measure?",
        text:
          "Subjective self-report (\u201Cparticipants felt their skin was brighter\u201D) is weaker than objective measures (corneometry, TEWL, image analysis, dermatologist grading). Both can appear in the same study \u2014 which one the marketing highlights is telling.",
      },
      {
        heading: "5. Was it peer-reviewed?",
        text:
          "\u201CPresented at a conference\u201D or a downloadable PDF on a brand's site is not peer review. A citation to a journal, with a DOI, is. If a claim can't be traced to a published paper, treat it as a marketing claim.",
      },
      {
        heading: "The takeaway",
        text:
          "You don't need to be a scientist to spot weak evidence. You just need to ask: who did the study, how big was it, what did they compare it to, what did they measure, and did anyone independent check the results.",
      },
    ],
    sources: [
      { text: "Guyatt GH, Haynes RB, Jaeschke RZ, et al. Users' Guides to the Medical Literature: XXV. Evidence-based medicine: principles for applying the Users' Guides to patient care. JAMA. 2000;284(10):1290\u20131296.", url: "https://doi.org/10.1001/jama.284.10.1290" },
      { text: "Ioannidis JPA. Why most published research findings are false. PLoS Med. 2005;2(8):e124.", url: "https://doi.org/10.1371/journal.pmed.0020124" },
    ],
  },
];
